import React, { FC, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { RouteComponentProps } from '@reach/router';
import { globalTheme, useMixin } from '@clds/component-theme';
import { Button, ButtonMenu } from '@cld/button';
import { Icon } from '@cld/icon';
import { RecipeContent, RecipeTags, PageHeader, usePlatformAnalytics, AnalyticsProvider, inMemoryFetchCookbookTransformationBySlug } from '../../common';
import { messages } from '../../i18n';
import { navigateToPage } from '../../utils/navigateToPage';
import { NotFoundRecipe } from '../../components/NotFoundRecipe/NotFoundRecipe';
import { StandaloneCookbookData } from '../../data/StandaloneCookbookData';
import { StickyPageHeader } from '../../components/StickyPageHeader/StickyPageHeader';
import { useScrollToTop } from '../../utils/useScrollToTop';
import { NavigationContainer, ContentHeader, PageWrapper, TitleWrapper, PageContainer, ContentWrapper, Footer, ContentInner } from './DetailsPage.styled';

export interface DetailsPageProps extends RouteComponentProps<{ slug: string }> {
  initialRecipe: StandaloneCookbookData['displayedRecipe'];
}

export const DetailsPage: FC<React.PropsWithChildren<DetailsPageProps>> = ({ slug }) => {
  useScrollToTop();
  const analytics = usePlatformAnalytics('cookbook recipe modal');
  const backIconColor = useMixin(globalTheme.palette.secondaryAlt);
  const languageChangeCallback = useCallback(
    (snippet: { language: string } | undefined) => snippet && analytics('navigate', snippet.language.toLowerCase()),
    [analytics]
  );
  const copyCallback = useCallback(
    (snippet: { language: string } | undefined) => snippet && analytics('copy', `copy ${snippet.language.toLowerCase()} code`),
    [analytics]
  );
  const handleBackClick = useCallback(() => {
    navigateToPage('/cookbook');
    analytics('navigate', 'back to cookbook');
  }, [analytics]);
  const footerSection = useMemo(
    () => (
      <Footer>
        <NavigationContainer>
          <Button data-test="navigate-back" variant="secondary" size="md" onClick={handleBackClick}>
            <FormattedMessage {...messages['pages.details.buttons.back']} />
          </Button>
        </NavigationContainer>
      </Footer>
    ),
    [handleBackClick]
  );
  const recipe = inMemoryFetchCookbookTransformationBySlug(slug || '');

  if (!recipe) {
    return <NotFoundRecipe onBackToCookbookClick={handleBackClick} />;
  }

  const transformation = recipe as NonNullable<StandaloneCookbookData['displayedRecipe']>;

  return (
    <AnalyticsProvider value="cookbook recipe modal">
      <PageWrapper>
        <StickyPageHeader>
          <PageHeader.SearchSubheader
            actionButton={
              <ButtonMenu data-test="back-action" onClick={handleBackClick}>
                <Icon color={backIconColor} name="arrowBack" size="xxl" />
              </ButtonMenu>
            }
          >
            <TitleWrapper data-test="mobile-page-header">{transformation.title}</TitleWrapper>
          </PageHeader.SearchSubheader>
        </StickyPageHeader>

        <PageContainer>
          <ContentWrapper>
            <ContentInner>
              <ContentHeader data-test="content-header">{transformation.title}</ContentHeader>
              <RecipeTags complexity={transformation.complexity} tags={transformation.categories.map(({ name }) => name)} />
              <RecipeContent languageChangeCallback={languageChangeCallback} copyCallback={copyCallback}>
                {transformation.content}
              </RecipeContent>
            </ContentInner>

            {footerSection}
          </ContentWrapper>
        </PageContainer>
      </PageWrapper>
    </AnalyticsProvider>
  );
};
